// Trivia Modal Component
// Admin interface for creating and editing trivia questions
// Follows the Section, Component & Manager System architecture

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { RevoltButton } from '../../../../components/RevoltButton';
import DropdownSelect from '../../../../components/DropdownSelect';
import '../../../../components/RevoltModal.css';
import './TriviaModal.css';

const QUESTION_TYPES = [
  { id: 'multiple-choice', label: 'Multiple Choice', description: 'Single selection from multiple options' },
  { id: 'true-false', label: 'True/False', description: 'Simple true or false question' },
  { id: 'multi-select', label: 'Multi-Select', description: 'Select all that apply' },
  { id: 'image-choice', label: 'Image Choice', description: 'Choose from image options' },
  { id: 'ordered-list', label: 'Ordered List', description: 'Arrange items in correct order' }
];

const DIFFICULTY_LEVELS = [
  { id: 'easy', label: 'Easy' },
  { id: 'medium', label: 'Medium' },
  { id: 'hard', label: 'Hard' }
];

export function TriviaModal({ isOpen, onClose, mode, initialData, onSubmit }) {

  const [formData, setFormData] = useState({
    question: '',
    questionType: 'multiple-choice',
    questionData: {
      options: [
        { text: '' },
        { text: '' }
      ],
      correctAnswer: 0,
      allowMultiple: false
    },
    tags: [],
    difficulty: 'medium',
    explanation: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';

      if (mode === 'edit' && initialData) {
        setFormData({
          question: initialData.question || '',
          questionType: initialData.questionType || 'multiple-choice',
          questionData: initialData.questionData || {
            options: [
              { text: '' },
              { text: '' }
            ],
            correctAnswer: 0,
            allowMultiple: false
          },
          tags: initialData.tags || [],
          difficulty: initialData.difficulty || 'medium',
          explanation: initialData.explanation || ''
        });
      } else {
        // Reset for create mode
        setFormData({
          question: '',
          questionType: 'multiple-choice',
          questionData: {
            options: [
              { text: '' },
              { text: '' }
            ],
            correctAnswer: 0,
            allowMultiple: false
          },
          tags: [],
          difficulty: 'medium',
          explanation: ''
        });
      }
      setTagInput('');
      setErrors({});
    } else {
      // Restore body scroll when modal closes
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, mode, initialData]);

  const handleQuestionTypeChange = (questionType) => {
    setFormData(prev => ({
      ...prev,
      questionType,
      questionData: getDefaultQuestionData(questionType)
    }));
  };

  const getDefaultQuestionData = (questionType) => {
    switch (questionType) {
      case 'multiple-choice':
        return {
          options: [
            { text: '' },
            { text: '' },
            { text: '' },
            { text: '' }
          ],
          correctAnswer: 0,
          allowMultiple: false
        };
      case 'true-false':
        return {
          options: [
            { text: 'True' },
            { text: 'False' }
          ],
          correctAnswer: 0,
          allowMultiple: false
        };
      case 'multi-select':
        return {
          options: [
            { text: '' },
            { text: '' },
            { text: '' }
          ],
          correctAnswers: [],
          allowMultiple: true,
          partialCredit: false
        };
      case 'image-choice':
        return {
          options: [
            { text: '', imageUrl: '' },
            { text: '', imageUrl: '' },
            { text: '', imageUrl: '' },
            { text: '', imageUrl: '' }
          ],
          correctAnswer: 0,
          allowMultiple: false,
          displayMode: 'grid'
        };
      case 'ordered-list':
        return {
          items: [
            { id: 'item1', text: '', correctOrder: 0 },
            { id: 'item2', text: '', correctOrder: 1 },
            { id: 'item3', text: '', correctOrder: 2 }
          ],
          shuffleItems: true,
          showNumbers: false
        };
      default:
        return {};
    }
  };

  const handleOptionChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      questionData: {
        ...prev.questionData,
        options: prev.questionData.options.map((option, i) =>
          i === index ? { ...option, [field]: value } : option
        )
      }
    }));
  };

  const handleCorrectAnswerToggle = (index) => {
    if (formData.questionType === 'multi-select') {
      setFormData(prev => {
        const correctAnswers = prev.questionData.correctAnswers || [];
        const newCorrectAnswers = correctAnswers.includes(index)
          ? correctAnswers.filter(i => i !== index)
          : [...correctAnswers, index];
        return {
          ...prev,
          questionData: {
            ...prev.questionData,
            correctAnswers: newCorrectAnswers
          }
        };
      });
    }
  };

  const handleOrderedItemChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      questionData: {
        ...prev.questionData,
        items: prev.questionData.items.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
  };

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      questionData: {
        ...prev.questionData,
        options: [...prev.questionData.options, { text: '' }]
      }
    }));
  };

  const removeOption = (index) => {
    if (formData.questionData.options.length <= 2) return; // Minimum 2 options

    setFormData(prev => ({
      ...prev,
      questionData: {
        ...prev.questionData,
        options: prev.questionData.options.filter((_, i) => i !== index),
        // Adjust correct answer if it was the removed option
        correctAnswer: prev.questionData.correctAnswer >= index && prev.questionData.correctAnswer > 0
          ? prev.questionData.correctAnswer - 1
          : prev.questionData.correctAnswer
      }
    }));
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.question.trim()) {
      newErrors.question = 'Question is required';
    }

    // Validate based on question type
    if (formData.questionType === 'multiple-choice') {
      if (!formData.questionData.options || formData.questionData.options.length < 2) {
        newErrors.options = 'At least 2 options are required';
      } else if (formData.questionData.options.some(option => !option.text?.trim())) {
        newErrors.options = 'All options must have text';
      }
      if (formData.questionData.correctAnswer === undefined || formData.questionData.correctAnswer === null) {
        newErrors.correctAnswer = 'Correct answer must be selected';
      }
    } else if (formData.questionType === 'true-false') {
      if (formData.questionData.correctAnswer === undefined || formData.questionData.correctAnswer === null) {
        newErrors.correctAnswer = 'Correct answer must be selected';
      }
    } else if (formData.questionType === 'multi-select') {
      if (!formData.questionData.options || formData.questionData.options.length < 2) {
        newErrors.options = 'At least 2 options are required';
      } else if (formData.questionData.options.some(option => !option.text?.trim())) {
        newErrors.options = 'All options must have text';
      }
      if (!formData.questionData.correctAnswers || formData.questionData.correctAnswers.length === 0) {
        newErrors.correctAnswers = 'At least one correct answer must be selected';
      }
    } else if (formData.questionType === 'image-choice') {
      if (!formData.questionData.options || formData.questionData.options.length < 2) {
        newErrors.options = 'At least 2 options are required';
      } else if (formData.questionData.options.some(option => !option.imageUrl?.trim())) {
        newErrors.options = 'All options must have image URLs';
      }
      if (formData.questionData.correctAnswer === undefined || formData.questionData.correctAnswer === null) {
        newErrors.correctAnswer = 'Correct answer must be selected';
      }
    } else if (formData.questionType === 'ordered-list') {
      if (!formData.questionData.items || formData.questionData.items.length < 2) {
        newErrors.items = 'At least 2 items are required';
      } else if (formData.questionData.items.some(item => !item.text?.trim())) {
        newErrors.items = 'All items must have text';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Failed to submit trivia:', error);
      // Don't close modal on error, let user try again
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-container modal-wide" style={{ maxWidth: '1400px' }}>
        <div className="modal-header">
          <h2 className="modal-title">{mode === 'edit' ? 'Edit Trivia Question' : 'Create Trivia Question'}</h2>
          <button className="modal-close" onClick={onClose}>
            <i className="fa-solid fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-content">
          <div className="trivia-modal-columns">
            {/* Column 1 - Question, Type, Category */}
            <div className="trivia-modal-col">
              {/* Question */}
              <div className="form-group">
                <label htmlFor="question">Question *</label>
                <textarea
                  id="question"
                  value={formData.question}
                  onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                  placeholder="Enter your trivia question..."
                  rows={4}
                  required
                />
                {errors.question && <span className="error-message">{errors.question}</span>}
              </div>

              {/* Question Type */}
              <div className="form-group">
                <label>Question Type *</label>
                <DropdownSelect
                  value={formData.questionType}
                  options={QUESTION_TYPES.map(type => ({ value: type.id, label: type.label }))}
                  onChange={(e) => handleQuestionTypeChange(e.target.value)}
                  placeholder="Select question type"
                />
                <div className="question-type-description">
                  {QUESTION_TYPES.find(t => t.id === formData.questionType)?.description}
                </div>
              </div>

              {/* Tags */}
              <div className="form-group">
                <label>Tags</label>
                <div className="tags-input">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value.replace(/\s+/g, '-'))}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add a tag..."
                  />
                  <button type="button" onClick={addTag}>
                    <i className="fa-solid fa-plus"></i>
                  </button>
                </div>
                <div className="tags-list">
                  {formData.tags.map(tag => (
                    <span key={tag} className="tag">
                      #{tag}
                      <button type="button" onClick={() => removeTag(tag)}>
                        <i className="fa-solid fa-times"></i>
                      </button>
                    </span>
                  ))}
                </div>
              </div>

            </div>

            {/* Column 2 - Difficulty, Explanation */}
            <div className="trivia-modal-col">

              {/* Difficulty */}
              <div className="form-group">
                <label>Difficulty</label>
                <DropdownSelect
                  value={formData.difficulty}
                  options={DIFFICULTY_LEVELS.map(level => ({ value: level.id, label: level.label }))}
                  onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value }))}
                  placeholder="Select difficulty"
                />
              </div>

              {/* Explanation */}
              <div className="form-group">
                <label htmlFor="explanation">Explanation</label>
                <textarea
                  id="explanation"
                  value={formData.explanation}
                  onChange={(e) => setFormData(prev => ({ ...prev, explanation: e.target.value }))}
                  placeholder="Explain the correct answer..."
                  rows={3}
                />
              </div>
            </div>

            {/* Column 3 - Type-Specific UI */}
            <div className="trivia-modal-col">
              <div className="type-specific-config">
                <h4>{QUESTION_TYPES.find(t => t.id === formData.questionType)?.label} Configuration</h4>

                {/* Multiple Choice Configuration */}
                {formData.questionType === 'multiple-choice' && (
                  <div className="type-config-section">
                    <div className="config-instructions">
                      Configure the multiple choice options and select the correct answer.
                    </div>

                    <div className="options-list">
                      {formData.questionData.options.map((option, index) => (
                        <div key={index} className="option-config-row">
                          <div className="option-radio">
                            <input
                              type="radio"
                              name="correctAnswer"
                              checked={formData.questionData.correctAnswer === index}
                              onChange={() => setFormData(prev => ({
                                ...prev,
                                questionData: {
                                  ...prev.questionData,
                                  correctAnswer: index
                                }
                              }))}
                            />
                          </div>
                          <div className="option-input-wrapper">
                            <input
                              type="text"
                              value={option.text}
                              onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                              placeholder={`Option ${String.fromCharCode(65 + index)}`}
                              className="option-input-full"
                            />
                          </div>
                          {formData.questionData.options.length > 2 && (
                            <button
                              type="button"
                              className="remove-option-btn"
                              onClick={() => removeOption(index)}
                              title="Remove option"
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="config-actions">
                      <RevoltButton
                        type="button"
                        variant="outline"
                        size="small"
                        onClick={addOption}
                      >
                        <i className="fa-solid fa-plus"></i>
                        Add Option
                      </RevoltButton>
                    </div>

                    {errors.options && <div className="error-message">{errors.options}</div>}
                    {errors.correctAnswer && <div className="error-message">{errors.correctAnswer}</div>}
                  </div>
                )}

                {/* True/False Configuration */}
                {formData.questionType === 'true-false' && (
                  <div className="type-config-section">
                    <div className="config-instructions">
                      Select whether the correct answer is True or False.
                    </div>

                    <div className="true-false-selector">
                      <label className="true-false-option-card">
                        <input
                          type="radio"
                          name="trueFalseAnswer"
                          checked={formData.questionData.correctAnswer === 0}
                          onChange={() => setFormData(prev => ({
                            ...prev,
                            questionData: {
                              ...prev.questionData,
                              correctAnswer: 0
                            }
                          }))}
                        />
                        <div className="option-card-content">
                          <div className="option-icon">✓</div>
                          <div className="option-label">True</div>
                        </div>
                      </label>

                      <label className="true-false-option-card">
                        <input
                          type="radio"
                          name="trueFalseAnswer"
                          checked={formData.questionData.correctAnswer === 1}
                          onChange={() => setFormData(prev => ({
                            ...prev,
                            questionData: {
                              ...prev.questionData,
                              correctAnswer: 1
                            }
                          }))}
                        />
                        <div className="option-card-content">
                          <div className="option-icon">✗</div>
                          <div className="option-label">False</div>
                        </div>
                      </label>
                    </div>

                    {errors.correctAnswer && <div className="error-message">{errors.correctAnswer}</div>}
                  </div>
                )}

                {/* Multi-Select Configuration */}
                {formData.questionType === 'multi-select' && (
                  <div className="type-config-section">
                    <div className="config-instructions">
                      Configure options and select all correct answers. Users must select all correct options to score 100%.
                    </div>

                    <div className="options-list">
                      {formData.questionData.options.map((option, index) => (
                        <div key={index} className="option-config-row">
                          <div className="option-checkbox">
                            <input
                              type="checkbox"
                              checked={formData.questionData.correctAnswers?.includes(index) || false}
                              onChange={() => handleCorrectAnswerToggle(index)}
                            />
                          </div>
                          <div className="option-input-wrapper">
                            <input
                              type="text"
                              value={option.text}
                              onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                              placeholder={`Option ${String.fromCharCode(65 + index)}`}
                              className="option-input-full"
                            />
                          </div>
                          {formData.questionData.options.length > 2 && (
                            <button
                              type="button"
                              className="remove-option-btn"
                              onClick={() => removeOption(index)}
                              title="Remove option"
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="config-actions">
                      <RevoltButton
                        type="button"
                        variant="outline"
                        size="small"
                        onClick={addOption}
                      >
                        <i className="fa-solid fa-plus"></i>
                        Add Option
                      </RevoltButton>
                    </div>

                    <div className="config-settings">
                      <label className="setting-toggle">
                        <input
                          type="checkbox"
                          checked={formData.questionData.partialCredit || false}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            questionData: {
                              ...prev.questionData,
                              partialCredit: e.target.checked
                            }
                          }))}
                        />
                        <span className="toggle-label">Allow partial credit for partially correct answers</span>
                      </label>
                    </div>

                    {errors.options && <div className="error-message">{errors.options}</div>}
                    {errors.correctAnswers && <div className="error-message">{errors.correctAnswers}</div>}
                  </div>
                )}

                {/* Image Choice Configuration */}
                {formData.questionType === 'image-choice' && (
                  <div className="type-config-section">
                    <div className="config-instructions">
                      Configure image options for users to choose from. Each option needs a valid image URL.
                    </div>

                    <div className="image-options-list">
                      {formData.questionData.options.map((option, index) => (
                        <div key={index} className="image-option-config">
                          <div className="image-option-header">
                            <div className="image-radio">
                              <input
                                type="radio"
                                name="imageCorrectAnswer"
                                checked={formData.questionData.correctAnswer === index}
                                onChange={() => setFormData(prev => ({
                                  ...prev,
                                  questionData: {
                                    ...prev.questionData,
                                    correctAnswer: index
                                  }
                                }))}
                              />
                            </div>
                            <div className="image-option-number">
                              Option {String.fromCharCode(65 + index)}
                              {formData.questionData.correctAnswer === index && (
                                <span className="correct-indicator">(Correct)</span>
                              )}
                            </div>
                            {formData.questionData.options.length > 2 && (
                              <button
                                type="button"
                                className="remove-option-btn"
                                onClick={() => removeOption(index)}
                                title="Remove option"
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            )}
                          </div>

                          <div className="image-input-group">
                            <input
                              type="url"
                              value={option.imageUrl}
                              onChange={(e) => handleOptionChange(index, 'imageUrl', e.target.value)}
                              placeholder="https://example.com/image.jpg"
                              className="image-url-input"
                            />
                            <input
                              type="text"
                              value={option.text}
                              onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                              placeholder="Optional label"
                              className="image-label-input"
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="config-actions">
                      <RevoltButton
                        type="button"
                        variant="outline"
                        size="small"
                        onClick={addOption}
                      >
                        <i className="fa-solid fa-plus"></i>
                        Add Image Option
                      </RevoltButton>
                    </div>

                    <div className="config-settings">
                      <label className="setting-select">
                        <span className="setting-label">Display Mode:</span>
                        <DropdownSelect
                          value={formData.questionData.displayMode || 'grid'}
                          options={[
                            { value: 'grid', label: 'Grid Layout' },
                            { value: 'list', label: 'List Layout' }
                          ]}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            questionData: {
                              ...prev.questionData,
                              displayMode: e.target.value
                            }
                          }))}
                          placeholder="Select display mode"
                        />
                      </label>
                    </div>

                    {errors.options && <div className="error-message">{errors.options}</div>}
                    {errors.correctAnswer && <div className="error-message">{errors.correctAnswer}</div>}
                  </div>
                )}

                {/* Ordered List Configuration */}
                {formData.questionType === 'ordered-list' && (
                  <div className="type-config-section">
                    <div className="config-instructions">
                      Configure the items that users need to arrange in the correct order. The current order shown here represents the correct sequence.
                    </div>

                    <div className="ordered-items-list">
                      {formData.questionData.items.map((item, index) => (
                        <div key={item.id || index} className="ordered-item-config">
                          <div className="item-order-indicator">
                            <span className="order-number">{index + 1}</span>
                            <span className="order-label">Correct Position</span>
                          </div>
                          <div className="item-text-input">
                            <input
                              type="text"
                              value={item.text}
                              onChange={(e) => handleOrderedItemChange(index, 'text', e.target.value)}
                              placeholder={`Step or item ${index + 1}`}
                              className="ordered-item-input"
                            />
                          </div>
                          {formData.questionData.items.length > 2 && (
                            <button
                              type="button"
                              className="remove-option-btn"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  questionData: {
                                    ...prev.questionData,
                                    items: prev.questionData.items.filter((_, i) => i !== index)
                                  }
                                }));
                              }}
                              title="Remove item"
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="config-actions">
                      <RevoltButton
                        type="button"
                        variant="outline"
                        size="small"
                        onClick={() => {
                          const newItem = {
                            id: `item${Date.now()}`,
                            text: '',
                            correctOrder: formData.questionData.items.length
                          };
                          setFormData(prev => ({
                            ...prev,
                            questionData: {
                              ...prev.questionData,
                              items: [...prev.questionData.items, newItem]
                            }
                          }));
                        }}
                      >
                        <i className="fa-solid fa-plus"></i>
                        Add Item
                      </RevoltButton>
                    </div>

                    <div className="config-settings">
                      <label className="setting-toggle">
                        <input
                          type="checkbox"
                          checked={formData.questionData.shuffleItems || false}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            questionData: {
                              ...prev.questionData,
                              shuffleItems: e.target.checked
                            }
                          }))}
                        />
                        <span className="toggle-label">Shuffle items when displayed to users (recommended)</span>
                      </label>

                      <label className="setting-toggle">
                        <input
                          type="checkbox"
                          checked={formData.questionData.showNumbers || false}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            questionData: {
                              ...prev.questionData,
                              showNumbers: e.target.checked
                            }
                          }))}
                        />
                        <span className="toggle-label">Show order numbers during user interaction</span>
                      </label>
                    </div>

                    {errors.items && <div className="error-message">{errors.items}</div>}
                  </div>
                )}

              </div>
            </div>
          </div>
        </form>

        <div className="trivia-modal-footer">
          <RevoltButton variant="outline" onClick={onClose}>
            Cancel
          </RevoltButton>
          <RevoltButton
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : (mode === 'edit' ? 'Update Question' : 'Create Question')}
          </RevoltButton>
        </div>
      </div>
    </div>,
    document.body
  );
}